'use client';

import { useId } from '@react-aria/utils';
import { type ElementType, type ReactNode } from 'react';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../../types/component';
import {
  type ResponsiveBreakpoint,
  type ResponsiveOption,
} from '../../../types/responsiveOption';
import { resolveResponsiveProps } from '../../../utils/resolveResponsiveProps';
import { tv } from '../../../utils/tv';
import { useLoadingAriaLabelledby } from '../../hooks/useLoadingAnnouncement';
import { elevationStyles, type Elevation } from '../../styles/elevation';
import { mapResponsiveOption } from '../../utils/mapResponsiveOption';
import { warnMissingLoadingAriaLabel } from '../../utils/warnMissingLoadingAriaLabel';
import { Pressable } from '../pressable/Pressable';

export type ButtonBaseSize = 'sm' | 'md' | 'lg' | 'xl';
export type ButtonBaseVariant = 'filled' | 'outlined' | 'ghost';
export type ButtonBaseColor =
  | 'accent-primary'
  | 'accent-secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'static-white';

const BUTTON_SIZE_MAPPING_SPINNER_SIZE: Record<ButtonBaseSize, 'sm' | 'md'> = {
  sm: 'sm',
  md: 'sm',
  lg: 'md',
  xl: 'md',
};

const buttonBaseStyles = tv({
  slots: {
    root: [
      'appearance-none',
      'm-0',
      'p-0',
      'border-none',
      'bg-transparent',
      'no-underline',
      'inline-flex',
      'items-center',
      'justify-center',
    ],
  },
  variants: {
    variant: {
      filled: { root: [] },
      outlined: { root: ['border-solid', 'border-sm'] },
      ghost: { root: [] },
    },
    color: {
      'accent-primary': {},
      'accent-secondary': {},
      success: {},
      error: {},
      info: {},
      warning: {},
      'static-white': {},
    },
    size: {
      sm: { root: ['z--button-base-sm-height', 'z--button-base-sm-radius'] },
      md: { root: ['z--button-base-md-height', 'z--button-base-md-radius'] },
      lg: { root: ['z--button-base-lg-height', 'z--button-base-lg-radius'] },
      xl: { root: ['z--button-base-xl-height', 'z--button-base-xl-radius'] },
    },
    sizeXs: {
      sm: {
        root: ['xs:z--button-base-sm-height', 'xs:z--button-base-sm-radius'],
      },
      md: {
        root: ['xs:z--button-base-md-height', 'xs:z--button-base-md-radius'],
      },
      lg: {
        root: ['xs:z--button-base-lg-height', 'xs:z--button-base-lg-radius'],
      },
      xl: {
        root: ['xs:z--button-base-xl-height', 'xs:z--button-base-xl-radius'],
      },
    },
    sizeSm: {
      sm: {
        root: ['sm:z--button-base-sm-height', 'sm:z--button-base-sm-radius'],
      },
      md: {
        root: ['sm:z--button-base-md-height', 'sm:z--button-base-md-radius'],
      },
      lg: {
        root: ['sm:z--button-base-lg-height', 'sm:z--button-base-lg-radius'],
      },
      xl: {
        root: ['sm:z--button-base-xl-height', 'sm:z--button-base-xl-radius'],
      },
    },
    sizeMd: {
      sm: {
        root: ['md:z--button-base-sm-height', 'md:z--button-base-sm-radius'],
      },
      md: {
        root: ['md:z--button-base-md-height', 'md:z--button-base-md-radius'],
      },
      lg: {
        root: ['md:z--button-base-lg-height', 'md:z--button-base-lg-radius'],
      },
      xl: {
        root: ['md:z--button-base-xl-height', 'md:z--button-base-xl-radius'],
      },
    },
    sizeLg: {
      sm: {
        root: ['lg:z--button-base-sm-height', 'lg:z--button-base-sm-radius'],
      },
      md: {
        root: ['lg:z--button-base-md-height', 'lg:z--button-base-md-radius'],
      },
      lg: {
        root: ['lg:z--button-base-lg-height', 'lg:z--button-base-lg-radius'],
      },
      xl: {
        root: ['lg:z--button-base-xl-height', 'lg:z--button-base-xl-radius'],
      },
    },
    sizeXl: {
      sm: {
        root: ['xl:z--button-base-sm-height', 'xl:z--button-base-sm-radius'],
      },
      md: {
        root: ['xl:z--button-base-md-height', 'xl:z--button-base-md-radius'],
      },
      lg: {
        root: ['xl:z--button-base-lg-height', 'xl:z--button-base-lg-radius'],
      },
      xl: {
        root: ['xl:z--button-base-xl-height', 'xl:z--button-base-xl-radius'],
      },
    },
  },
  compoundVariants: [
    // Filled — solid background + contrasting foreground
    {
      variant: 'filled',
      color: 'accent-primary',
      class: {
        root: ['bg-accent-primary-main', 'text-accent-primary-main-foreground'],
      },
    },
    {
      variant: 'filled',
      color: 'accent-secondary',
      class: {
        root: [
          'bg-accent-secondary-main',
          'text-accent-secondary-main-foreground',
        ],
      },
    },
    {
      variant: 'filled',
      color: 'success',
      class: { root: ['bg-success-main', 'text-success-main-foreground'] },
    },
    {
      variant: 'filled',
      color: 'error',
      class: { root: ['bg-error-main', 'text-error-main-foreground'] },
    },
    {
      variant: 'filled',
      color: 'info',
      class: { root: ['bg-info-main', 'text-info-main-foreground'] },
    },
    {
      variant: 'filled',
      color: 'warning',
      class: { root: ['bg-warning-main', 'text-warning-main-foreground'] },
    },
    {
      variant: 'filled',
      color: 'static-white',
      class: { root: ['bg-static-white-main', 'text-static-black-main'] },
    },
    // Outlined — border + foreground
    {
      variant: 'outlined',
      color: 'accent-primary',
      class: {
        root: ['border-accent-primary-main', 'text-accent-primary-main'],
      },
    },
    {
      variant: 'outlined',
      color: 'accent-secondary',
      class: {
        root: ['border-accent-secondary-main', 'text-accent-secondary-main'],
      },
    },
    {
      variant: 'outlined',
      color: 'success',
      class: { root: ['border-success-main', 'text-success-main'] },
    },
    {
      variant: 'outlined',
      color: 'error',
      class: { root: ['border-error-main', 'text-error-main'] },
    },
    {
      variant: 'outlined',
      color: 'info',
      class: { root: ['border-info-main', 'text-info-main'] },
    },
    {
      variant: 'outlined',
      color: 'warning',
      class: { root: ['border-warning-main', 'text-warning-main'] },
    },
    {
      variant: 'outlined',
      color: 'static-white',
      class: { root: ['border-static-white-main', 'text-static-white-main'] },
    },
    // Ghost — foreground only
    {
      variant: 'ghost',
      color: 'accent-primary',
      class: { root: 'text-accent-primary-main' },
    },
    {
      variant: 'ghost',
      color: 'accent-secondary',
      class: { root: 'text-accent-secondary-main' },
    },
    {
      variant: 'ghost',
      color: 'success',
      class: { root: 'text-success-main' },
    },
    {
      variant: 'ghost',
      color: 'error',
      class: { root: 'text-error-main' },
    },
    {
      variant: 'ghost',
      color: 'info',
      class: { root: 'text-info-main' },
    },
    {
      variant: 'ghost',
      color: 'warning',
      class: { root: 'text-warning-main' },
    },
    {
      variant: 'ghost',
      color: 'static-white',
      class: { root: 'text-static-white-main' },
    },
  ],
});

export type SpinnerSizeValue =
  (typeof BUTTON_SIZE_MAPPING_SPINNER_SIZE)[keyof typeof BUTTON_SIZE_MAPPING_SPINNER_SIZE];

export type ButtonBaseRenderProps = {
  isFocused: boolean;
  rootId: string;
  loadingIndicatorId: string;
  ariaLabelledby: string | undefined;
  spinnerSize: ResponsiveOption<SpinnerSizeValue>;
};

/** Shared props owned by ButtonBase and extended by Button and IconButton. */
export type ButtonBaseSharedProps = {
  /** Visual style variant. `filled` applies a solid background; `outlined` adds a border with a transparent background; `ghost` has neither background nor border in its default state. */
  variant?: ButtonBaseVariant;
  /** Colour scheme. @default 'accent-primary' */
  color?: ButtonBaseColor;
  /** Size. Accepts a scalar value or a responsive object. @default 'md' */
  size?: ResponsiveOption<ButtonBaseSize>;
  /** Whether the button is disabled. @default false */
  isDisabled?: boolean;
  /** Whether the button is in a loading state. @default false */
  isLoading?: boolean;
  /**
   * Accessible label for the loading indicator (spinner).
   * Required when `isLoading` is `true`.
   */
  loadingAriaLabel?: string;
};

type ButtonBaseOwnProps = ButtonBaseSharedProps & {
  /** Component display name used in dev-mode warnings. */
  componentName: string;
  /**
   * Elevation depth. Applies a drop-shadow and, for non-filled variants, an elevated surface colour. Omit to apply no elevation.
   */
  elevation?: Elevation;
  /** Render prop receiving derived interaction state and ids. */
  children: (renderProps: ButtonBaseRenderProps) => ReactNode;
};

const DEFAULT_ELEMENT_TYPE = 'button';
type DefaultElementType = typeof DEFAULT_ELEMENT_TYPE;
type OmittedKeys = 'disabled';

type ButtonBaseProps<RootComponent extends ElementType = DefaultElementType> =
  PolymorphicComponentProps<ButtonBaseOwnProps, RootComponent, OmittedKeys>;

type ButtonBaseComponent = PolymorphicComponent<
  ButtonBaseOwnProps,
  DefaultElementType,
  OmittedKeys
>;

const ButtonBase: ButtonBaseComponent = (props) => {
  const finalProps = props as ButtonBaseProps;
  const {
    id,
    component: Root = DEFAULT_ELEMENT_TYPE,
    variant = 'filled',
    color = 'accent-primary',
    size: sizeProp,
    elevation,
    isDisabled = false,
    isLoading = false,
    loadingAriaLabel,
    componentName,
    className,
    ref,
    children,
    ...rest
  } = finalProps;

  warnMissingLoadingAriaLabel(componentName, isLoading, loadingAriaLabel);

  const { defaultOption: defaultSize, responsiveOption: responsiveSize } =
    resolveResponsiveProps(sizeProp);
  const resolvedSize = defaultSize ?? 'md';

  const spinnerSize = mapResponsiveOption(
    resolvedSize,
    responsiveSize as ResponsiveBreakpoint<ButtonBaseSize> | undefined,
    BUTTON_SIZE_MAPPING_SPINNER_SIZE
  );

  const isElevated = Boolean(
    elevation && variant !== 'filled' && color !== 'static-white'
  );

  const interactionColors = {
    backgroundColor: color,
    bgMode: variant === 'filled' ? 'filled' : isElevated ? 'elevated' : 'blank',
    focusColor: color === 'static-white' ? 'static-white' : 'default',
  } as const;

  const elevationClass = elevation
    ? elevationStyles({ elevation, withSurface: isElevated })
    : undefined;

  const rootId = useId(id);

  const { loadingIndicatorId, ariaLabelledby } = useLoadingAriaLabelledby({
    id: rootId,
    isLoading,
    ariaLabelledby: finalProps['aria-labelledby'],
    ariaLabel: finalProps['aria-label'],
  });

  return (
    <Pressable
      {...rest}
      component={Root}
      interactionColors={interactionColors}
      isDisabled={isDisabled}
      isLoading={isLoading}
      id={rootId}
      ref={ref}
      aria-labelledby={ariaLabelledby}
      className={buttonBaseStyles({
        variant,
        color,
        size: resolvedSize,
        sizeXs: responsiveSize?.xs,
        sizeSm: responsiveSize?.sm,
        sizeMd: responsiveSize?.md,
        sizeLg: responsiveSize?.lg,
        sizeXl: responsiveSize?.xl,
      }).root({
        class: [elevationClass, className],
      })}
    >
      {(state) =>
        children({
          isFocused: state.isFocused,
          rootId,
          loadingIndicatorId,
          ariaLabelledby,
          spinnerSize,
        })
      }
    </Pressable>
  );
};

ButtonBase.displayName = 'ButtonBase';

export { ButtonBase, type ButtonBaseProps, type ButtonBaseOwnProps };
